import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { apiService } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { useAuthStore } from '../store/authStore';
import { isCmsSellerGroupUser } from '../utils/cmsSellerGroups';
import { Coins, RefreshCw } from 'lucide-react';

const brl = (n: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(n || 0);

interface PackageRow {
  id: string;
  label: string;
  credits: number;
  /** Total a pagar (créditos × preço unitário). */
  priceBrl: number;
  /** Preço por crédito (R$), quando enviado pela API. */
  pricePerCreditBrl?: number;
  active?: boolean;
}

export default function BuyCredits() {
  const { user, isAdmin, isSocio, isMaster } = useAuth();
  const setUser = useAuthStore((s) => s.setUser);
  const canBuy = isCmsSellerGroupUser(user);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [catalog, setCatalog] = useState<{
    eligible: boolean;
    creditSalesEnabled: boolean;
    packages: PackageRow[];
    mercadoPagoReady: boolean;
    updePixReady: boolean;
    lynxReady: boolean;
  } | null>(null);

  const [selectedId, setSelectedId] = useState<string>('');
  /** Opcional: master/sócio/admin credita outro ID MySQL da hierarquia (POST buyerMemberId). */
  const [creditForUserId, setCreditForUserId] = useState<string>('');
  const [payerDocument, setPayerDocument] = useState<string>('');
  const [payBusy, setPayBusy] = useState(false);
  const [pix, setPix] = useState<{
    pixCopyPaste: string;
    qrImageUrl?: string | null;
    orderId: string;
    totalBrl: number;
    credits: number;
  } | null>(null);
  /** pending | paid | completed | failed | stalled */
  const [pixOrderStatus, setPixOrderStatus] = useState<string | null>(null);

  /** PIX em aberto: evita novo pagamento / troca de pacote até o fluxo terminar. */
  const pixOpenOrder = Boolean(
    pix?.orderId && !['failed', 'completed', 'stalled'].includes(String(pixOrderStatus || ''))
  );

  /** Spinner só depois do pagamento confirmado (`paid`), enquanto credita no backend. */
  const pixPostPaymentOverlay = Boolean(pix?.orderId && pixOrderStatus === 'paid');

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const res = await apiService.getCreditPurchaseCatalog();
      if (!res?.success) {
        setErr(res?.error || 'Não foi possível carregar o catálogo.');
        setCatalog(null);
        return;
      }
      setCatalog(res.data);
      const pkgs = (res.data && res.data.packages) || [];
      setSelectedId((prev) => prev || (pkgs[0] ? String(pkgs[0].id) : ''));
    } catch (e: any) {
      setErr(e?.response?.data?.error || e?.message || 'Erro ao carregar.');
      setCatalog(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canBuy) {
      void load();
    } else {
      setLoading(false);
    }
  }, [canBuy, load]);

  useEffect(() => {
    if (!pix?.orderId) {
      setPixOrderStatus(null);
      return undefined;
    }

    let cancelled = false;
    const started = Date.now();
    const maxMs = 25 * 60 * 1000;
    const ivRef = { current: null as ReturnType<typeof setInterval> | null };

    const refreshProfileCredits = async () => {
      try {
        const profile = await apiService.getProfile();
        const u = useAuthStore.getState().user;
        if (!cancelled && profile && u) {
          setUser({
            ...u,
            credits:
              profile.credits != null && Number.isFinite(Number(profile.credits))
                ? Number(profile.credits)
                : u.credits
          });
        }
      } catch {
        /* opcional */
      }
    };

    const pollOnce = async () => {
      if (cancelled) return;
      if (Date.now() - started > maxMs) {
        setPixOrderStatus('stalled');
        setErr(
          'Ainda não recebemos a confirmação automática nesta página. Se o PIX já foi debitado, guarde o comprovativo e fale com o suporte — os créditos podem ser creditados nos bastidores.'
        );
        if (ivRef.current) clearInterval(ivRef.current);
        return;
      }
      try {
        const res = await apiService.getCreditPurchaseOrderStatus(pix.orderId);
        if (cancelled || !res?.success || !res.data) return;
        const st = String(res.data.status || '').trim();
        if (st) setPixOrderStatus(st);
        if (st === 'completed') {
          setErr(null);
          setSuccessMsg(
            `Pagamento confirmado. ${res.data.credits != null ? `${res.data.credits} créditos ` : ''}creditados na sua conta.`
          );
          setPix(null);
          setPixOrderStatus('completed');
          await refreshProfileCredits();
          void load();
          if (ivRef.current) clearInterval(ivRef.current);
          return;
        }
        if (st === 'failed') {
          setPixOrderStatus('failed');
          setErr(
            res.data.failureReason
              ? `Compra não concluída: ${res.data.failureReason}`
              : 'Pagamento ou créditos não concluídos. Contacte o suporte.'
          );
          if (ivRef.current) clearInterval(ivRef.current);
          return;
        }
        if (st === 'paid') {
          setSuccessMsg((prev) => {
            if (prev && prev.includes('creditando')) return prev;
            return 'Pagamento recebido. Creditando créditos — aguarde nesta página alguns instantes…';
          });
        }
      } catch {
        /* rede transitória */
      }
    };

    void pollOnce();
    ivRef.current = setInterval(pollOnce, 3000);
    return () => {
      cancelled = true;
      if (ivRef.current) clearInterval(ivRef.current);
    };
  }, [pix?.orderId, load, setUser]);

  useEffect(() => {
    if (!pixPostPaymentOverlay) return undefined;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [pixPostPaymentOverlay]);

  const selectedPkg = useMemo(() => {
    const pkgs = catalog?.packages || [];
    return pkgs.find((p) => String(p.id) === String(selectedId)) || null;
  }, [catalog, selectedId]);

  const payMercadoPago = async () => {
    if (!selectedPkg) return;
    setPayBusy(true);
    setErr(null);
    setSuccessMsg(null);
    setPix(null);
    setPixOrderStatus(null);
    try {
      const buyerOpt = parseInt(String(creditForUserId || '').trim(), 10);
      const res = await apiService.createCreditPurchaseMercadoPago({
        packageId: String(selectedPkg.id),
        ...(Number.isFinite(buyerOpt) && buyerOpt > 0 ? { buyerMemberId: buyerOpt } : {})
      });
      if (!res?.success) {
        setErr(res?.error || 'Não foi possível iniciar o pagamento.');
        return;
      }
      const url = res.initPoint || res.sandboxInitPoint;
      if (url) {
        window.location.href = url;
        return;
      }
      setErr('Mercado Pago não retornou URL de checkout.');
    } catch (e: any) {
      setErr(e?.response?.data?.error || e?.message || 'Erro ao criar pagamento.');
    } finally {
      setPayBusy(false);
    }
  };

  const payUpdePix = async () => {
    if (!selectedPkg) return;
    const docDigits = payerDocument.replace(/\D/g, '');
    if (docDigits.length !== 11 && docDigits.length !== 14) {
      setErr('Informe o CPF (11 dígitos) ou CNPJ (14 dígitos) do pagador.');
      return;
    }
    setPayBusy(true);
    setErr(null);
    setSuccessMsg(null);
    setPix(null);
    setPixOrderStatus(null);
    try {
      const buyerOpt = parseInt(String(creditForUserId || '').trim(), 10);
      const res = await apiService.createCreditPurchaseUpdePix({
        packageId: String(selectedPkg.id),
        payerDocument: docDigits,
        ...(Number.isFinite(buyerOpt) && buyerOpt > 0 ? { buyerMemberId: buyerOpt } : {})
      });
      if (!res?.success) {
        setErr(res?.error || 'Não foi possível gerar o PIX.');
        return;
      }
      setPixOrderStatus('pending');
      setPix({
        pixCopyPaste: res.pixCopyPaste,
        qrImageUrl: res.qrImageUrl || null,
        orderId: res.orderId,
        totalBrl: res.totalBrl,
        credits: res.credits
      });
      setSuccessMsg(
        'PIX gerado. Pague no app do banco e mantenha esta página aberta até a confirmação — estamos verificando automaticamente.'
      );
    } catch (e: any) {
      setErr(e?.response?.data?.error || e?.message || 'Erro ao gerar PIX.');
    } finally {
      setPayBusy(false);
    }
  };

  const payLynx = async () => {
    if (!selectedPkg) return;
    // Lynx: CPF/CNPJ opcional — só envia se o utilizador preencheu (ex.: UpdePix visível na mesma tela)
    const docDigits = payerDocument.replace(/\D/g, '');
    if (docDigits && docDigits.length !== 11 && docDigits.length !== 14) {
      setErr('Se informar documento, use CPF (11) ou CNPJ (14) dígitos. Na Lynx o CPF é opcional.');
      return;
    }
    setPayBusy(true);
    setErr(null);
    setSuccessMsg(null);
    setPix(null);
    setPixOrderStatus(null);
    try {
      const buyerOpt = parseInt(String(creditForUserId || '').trim(), 10);
      const res = await apiService.createCreditPurchaseLynx({
        packageId: String(selectedPkg.id),
        ...(docDigits ? { payerDocument: docDigits } : {}),
        ...(Number.isFinite(buyerOpt) && buyerOpt > 0 ? { buyerMemberId: buyerOpt } : {})
      });
      if (!res?.success) {
        setErr(res?.error || 'Não foi possível gerar o PIX Lynx.');
        return;
      }
      setPixOrderStatus('pending');
      setPix({
        pixCopyPaste: res.pixCopyPaste,
        qrImageUrl: res.qrImageUrl || null,
        orderId: res.orderId,
        totalBrl: res.totalBrl,
        credits: res.credits
      });
      setSuccessMsg(
        'PIX Lynx gerado. Pague no app do banco e mantenha esta página aberta até a confirmação.'
      );
    } catch (e: any) {
      setErr(e?.response?.data?.error || e?.message || 'Erro ao gerar PIX Lynx.');
    } finally {
      setPayBusy(false);
    }
  };

  if (!canBuy) {
    return (
      <div className="p-6">
        <Card>
          <CardHeader>
            <CardTitle>Comprar créditos</CardTitle>
            <CardDescription>
              Disponível apenas para contas de venda (sócio, master, revendedor ou sub-revendedor), com venda automática
              de créditos ativada pelo master em Configurações → Pagamentos.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Coins className="w-7 h-7 text-amber-500" />
            Comprar créditos
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            A opção depende do seu <strong>owner_id</strong> o sistema busca o CMS
            ligado a esse ID e só exibe compra se lá estiver venda de créditos ativa com pacotes. O pagamento usa o
            Mercado Pago / UpdePix configurados nesse CMS do seu master; os créditos são somados na conta do comprador
            após a confirmação.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading || pixOpenOrder}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Atualizar
        </Button>
      </div>

      {successMsg && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-100 px-3 py-2 text-sm">
          {successMsg}
        </div>
      )}

      {err && (
        <div className="rounded-md border border-red-200 bg-red-50 text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-200 px-3 py-2 text-sm">
          {err}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Pacotes</CardTitle>
          <CardDescription>
            {!catalog?.eligible && 'Você precisa estar vinculado à hierarquia do dono deste CMS.'}
            {catalog?.eligible && !catalog?.creditSalesEnabled && 'O master ainda não ativou a venda automática de créditos.'}
            {catalog?.eligible &&
              catalog?.creditSalesEnabled &&
              (!catalog?.packages || catalog.packages.length === 0) &&
              'Nenhum pacote cadastrado. Peça ao master para configurar em Configurações → Pagamentos.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading ? (
            <p className="text-sm text-muted-foreground">Carregando…</p>
          ) : catalog && catalog.eligible && catalog.creditSalesEnabled && catalog.packages?.length ? (
            <>
              {(isAdmin() || isSocio() || isMaster()) && (
                <div className="rounded-md border border-dashed border-border bg-muted/30 p-3 space-y-2">
                  <label className="text-sm font-medium" htmlFor="credit-for-user-id">
                    Creditar em outro usuário (opcional)
                  </label>
                  <p className="text-xs text-muted-foreground">
                    Informe o ID numérico do painel (MySQL) do revendedor. Deixe em branco para creditar a si mesmo.
                    Apenas hierarquia sob o seu usuário.
                  </p>
                  <input
                    id="credit-for-user-id"
                    type="number"
                    min={1}
                    className="w-full max-w-xs rounded border bg-background px-2 py-1.5 text-sm"
                    placeholder="Ex.: 12345"
                    value={creditForUserId}
                    onChange={(e) => setCreditForUserId(e.target.value)}
                    disabled={pixOpenOrder}
                  />
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {catalog.packages.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setSelectedId(String(p.id))}
                    disabled={pixOpenOrder}
                    className={`text-left rounded-lg border p-4 transition ${
                      String(selectedId) === String(p.id)
                        ? 'border-primary ring-2 ring-primary/30'
                        : 'border-border hover:border-primary/50'
                    } ${pixOpenOrder ? 'opacity-60 pointer-events-none' : ''}`}
                  >
                    <div className="font-semibold">{p.label}</div>
                    <div className="text-sm text-muted-foreground mt-1">
                      {p.credits} créditos ×{' '}
                      {brl(
                        p.pricePerCreditBrl != null
                          ? p.pricePerCreditBrl
                          : p.credits > 0
                            ? p.priceBrl / p.credits
                            : 0
                      )}
                    </div>
                    <div className="text-lg font-bold mt-2">{brl(p.priceBrl)}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">total a pagar</div>
                  </button>
                ))}
              </div>

              {catalog.updePixReady && (
              <div className="rounded-md border border-dashed border-border bg-muted/30 p-3 space-y-2">
                <label className="text-sm font-medium" htmlFor="credit-payer-document">
                  CPF ou CNPJ do pagador
                </label>
                <p className="text-xs text-muted-foreground">
                  Obrigatório para PIX (UpdePix) — somente números. Lynx não exige CPF.
                </p>
                <input
                  id="credit-payer-document"
                  type="text"
                  inputMode="numeric"
                  className="w-full max-w-xs rounded border bg-background px-2 py-1.5 text-sm"
                  placeholder="11 ou 14 dígitos"
                  value={payerDocument}
                  onChange={(e) => setPayerDocument(e.target.value)}
                  disabled={pixOpenOrder}
                />
              </div>
              )}

              <div className="flex flex-wrap gap-2 pt-2">
                <Button
                  onClick={() => void payMercadoPago()}
                  disabled={payBusy || pixOpenOrder || !selectedPkg || !catalog.mercadoPagoReady}
                >
                  Pagar com Mercado Pago
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => void payUpdePix()}
                  disabled={payBusy || pixOpenOrder || !selectedPkg || !catalog.updePixReady}
                >
                  Pagar com PIX (UpdePix)
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => void payLynx()}
                  disabled={payBusy || pixOpenOrder || !selectedPkg || !catalog.lynxReady}
                >
                  Pagar com PIX (Lynx)
                </Button>
              </div>
              {!catalog.mercadoPagoReady && (
                <p className="text-xs text-muted-foreground">Mercado Pago não está pronto para este CMS.</p>
              )}
              {!catalog.updePixReady && (
                <p className="text-xs text-muted-foreground">UpdePix não está pronto para este CMS.</p>
              )}
              {!catalog.lynxReady && (
                <p className="text-xs text-muted-foreground">Lynx não está pronto para este CMS.</p>
              )}
            </>
          ) : (
            <p className="text-sm text-muted-foreground">Nenhuma opção de compra disponível no momento.</p>
          )}
        </CardContent>
      </Card>

      {pix && (
        <Card className="relative overflow-hidden">
          <CardHeader>
            <CardTitle>PIX gerado</CardTitle>
            <CardDescription>
              Pedido {pix.orderId} · {pix.credits} créditos · {brl(pix.totalBrl)}
            </CardDescription>
          </CardHeader>
          <CardContent className="relative space-y-3">
            {pix.qrImageUrl && (
              <div className="flex justify-center">
                <img src={pix.qrImageUrl} alt="QR Code PIX" className="max-w-[220px] rounded border" loading="lazy" />
              </div>
            )}
            <textarea
              className="w-full min-h-[120px] rounded border bg-background p-2 text-sm font-mono"
              readOnly
              value={pix.pixCopyPaste}
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                void navigator.clipboard.writeText(pix.pixCopyPaste);
              }}
            >
              Copiar código PIX
            </Button>
            {pixPostPaymentOverlay && (
              <div
                className="absolute inset-0 z-10 flex items-center justify-center rounded-md border border-border bg-background/90 px-4 py-6 text-center backdrop-blur-sm"
                role="status"
                aria-live="polite"
              >
                <div className="max-w-sm space-y-3">
                  <div
                    className="mx-auto h-10 w-10 rounded-full border-2 border-muted border-t-primary animate-spin"
                    aria-hidden
                  />
                  <p className="text-sm font-semibold text-foreground">Pagamento recebido — creditando créditos…</p>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Não feche nem atualize esta página até aparecer a mensagem de sucesso. Pode levar alguns segundos.
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
